import * as Sentry from '@sentry/nextjs';
import getenv from 'getenv';

import type { ConversionId } from '@tmh-website/services/tracify';
import { isNonEmptyTrimmedString } from '@tmh-website/shared/helpers';

import type { HubSpotFormRendererValues } from './HubSpotFormRenderer.types';
import type { SubmitFormProps } from './hubspot-form-renderer.service';

const isInDevelopmentMode: boolean = getenv('NODE_ENV', process.env.NODE_ENV) === 'development';

/**
 * Fills the passed-in object with the hidden fields and their values.
 *
 * @param values - The values object that is to be extended with the hidden fields.
 *
 * @returns - A new object with all the entries from the entry `values` argument AND the hidden fields.
 */
export const fillValuesWithHiddenFields = (values: HubSpotFormRendererValues): HubSpotFormRendererValues => {
  const currentUrl: URL = new URL(window.location.href);
  const hiddenFieldsValues: HubSpotFormRendererValues = getUTMValuesFromURL(currentUrl);

  return {
    ...values,
    ...hiddenFieldsValues,
  };
};

/**
 * Resolves the most accurate `ConversionId` based on the passed-in values.
 *
 * @param values - An object compliant with the `SubmitFormProps['values']` interface.
 *
 * @throws Error - If no `ConversionId` has been found for the passed-in values (only in development mode).
 */
export const getConversionIdFromValues = (values: SubmitFormProps['values']): ConversionId => {
  const { company, email, first_name, firstname, firstName, last_name, lastname, lastName, phone } = values;

  const foundFirstName: string | undefined = [first_name, firstname, firstName].find(isNonEmptyTrimmedString);
  const foundLastName: string | undefined = [last_name, lastname, lastName].find(isNonEmptyTrimmedString);

  if (isNonEmptyTrimmedString(email)) {
    return email as ConversionId;
  } else if (isNonEmptyTrimmedString(phone)) {
    return phone as ConversionId;
  } else if (isNonEmptyTrimmedString(foundFirstName)) {
    return (
      isNonEmptyTrimmedString(foundLastName) ? `${foundFirstName} ${foundLastName}` : foundFirstName
    ) as ConversionId;
  } else if (isNonEmptyTrimmedString(foundLastName)) {
    return foundLastName as ConversionId;
  } else if (isNonEmptyTrimmedString(company)) {
    return company as ConversionId;
  }

  const error: Error = new Error(
    `Failed to resolve the 'ConversionID' from the passed-in 'values': ${JSON.stringify(values)}`
  );

  if (isInDevelopmentMode) {
    throw error;
  }

  try {
    Sentry.captureException(error, scope => scope.setContext('values', values));
  } catch (sentryError) {
    console.error('Failed to send the error to Sentry:', sentryError);
  }

  return 'unknown' as ConversionId;
};

/**
 * Extracts the UTM parameter values from the provided URL and returns them as an object.
 *
 * @param url - The URL from which to extract UTM parameter values.
 *
 * @returns - The UTM parameter values as an object (compliant with the HubSpotFormRendererValues).
 */
const getUTMValuesFromURL = (url: URL): HubSpotFormRendererValues => {
  const utmValues = {};

  for (const [key, value] of url.searchParams) {
    if (key.startsWith('utm_')) {
      utmValues[key] = value;
    }
  }

  return utmValues;
};
